import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';

import { EntityType, SegmentTrackEvent } from '@ninety/mobile/platform/services';
import { OneSignalActions, SegmentActions } from '@ninety/mobile/platform/state';

import { NotificationItemType } from '../models/notification-item-type';

import { NotificationFeedActions } from './notification-feed-state.actions';

/**
 * Maps a notification's underlying item type to the BI-owned {@link EntityType} so
 * `notification_clicked` / `notification_read_status_toggled` carry the v3 `entityId` + `entityType`
 * entity payload. Cascading messages are headlines, so they bucket to `headline`.
 */
const NOTIFICATION_ITEM_ENTITY_TYPE: Record<NotificationItemType, EntityType> = {
  [NotificationItemType.rock]: 'rock',
  [NotificationItemType.milestone]: 'milestone',
  [NotificationItemType.todo]: 'todo',
  [NotificationItemType.shortTermIssue]: 'issue',
  [NotificationItemType.longTermIssue]: 'issue',
  [NotificationItemType.headline]: 'headline',
  [NotificationItemType.cascadingMessage]: 'headline',
};

@Injectable()
export class NotificationFeedPageAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  /** Track foreground push notification received in Segment. */
  trackPushNotificationReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneSignalActions.pushNotificationReceived),
      map(({ notificationId, additionalData }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.PUSH_NOTIFICATION_RECEIVED,
          params: {
            notificationId,
            entityType: additionalData?.entityType,
            reason: additionalData?.reason,
            receivedInForeground: true,
          },
        })
      )
    )
  );

  /** Track when the notification feed is opened (bell icon tapped). */
  trackOpeningNotificationFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_OPENED,
        })
      )
    )
  );

  /** Track when a notification card is clicked to view detail. */
  trackNotificationClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.openItemDetail),
      map(({ notification }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_CLICKED,
          params: {
            entityId: notification.itemId,
            entityType: NOTIFICATION_ITEM_ENTITY_TYPE[notification.itemType],
            notificationId: notification.id,
            itemType: notification.itemType,
            itemId: notification.itemId,
            reason: notification.reason,
            isRead: !!notification.markedReadAt,
            teamId: notification.teamId,
          },
        })
      )
    )
  );

  /** Track when the notification feed filter is changed. */
  trackNotificationFeedFilterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.setFilterOption),
      map(({ filterBy }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_FILTER_CHANGED,
          params: {
            filter: filterBy,
          },
        })
      )
    )
  );

  /** Track when all notifications are marked as read. */
  trackNotificationMarkAllAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.markAllAsRead),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_MARK_ALL_AS_READ,
        })
      )
    )
  );

  /** Track when a notification's read/unread status is toggled. */
  trackNotificationReadStatusToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.toggleNotificationReadStatus),
      map(({ notification }) => {
        const newStatus = !notification.markedReadAt;
        return SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_READ_STATUS_TOGGLED,
          params: {
            entityId: notification.itemId,
            entityType: NOTIFICATION_ITEM_ENTITY_TYPE[notification.itemType],
            notificationId: notification.id,
            itemType: notification.itemType,
            itemId: notification.itemId,
            reason: notification.reason,
            action: newStatus ? 'mark_as_read' : 'mark_as_unread',
            teamId: notification.teamId,
            isRead: newStatus,
          },
        });
      })
    )
  );
}
